import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Info, Loader2, Mic2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import MicPicker, { useSavedMic } from "@/components/creator/MicPicker";
import useVocalRecorder from "@/components/creator/useVocalRecorder";
import BackingPrep from "@/components/creator/sing/BackingPrep";
import LyricsPrompter from "@/components/creator/sing/LyricsPrompter";
import SingOptions from "@/components/creator/sing/SingOptions";
import SingRecorder from "@/components/creator/sing/SingRecorder";
import useSingSession from "@/components/creator/sing/useSingSession";
import useBacking from "@/components/creator/sing/useBacking";
import VersionPills, { sortedGenerated } from "@/components/creator/sing/VersionPills";
import { getEngine } from "@/lib/engineSingleton";
import { getProject, listVoiceProfiles, uploadProjectTake } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

function usePlayhead(engine, active) {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => { if (active || engine.playing) setT(engine.getPlayhead()); }, 250);
    return () => clearInterval(id);
  }, [engine, active]);
  return t;
}

function SingSession({ project, version }) {
  const navigate = useNavigate();
  const [engine] = useState(() => getEngine());
  const { backing, prepare, busy } = useBacking(project, version);
  const ready = backing?.status === "done";
  const [mode, setMode] = useState(null);
  const [guide, setGuide] = useState(false);
  const [profiles, setProfiles] = useState(null);
  const [singerId, setSingerId] = useState(null);
  const [keeping, setKeeping] = useState(false);
  const [deviceId, setDeviceId] = useSavedMic();
  const title = project.title || "Song";

  useEffect(() => {
    listVoiceProfiles().then((rows) => {
      setProfiles(rows);
      const lead = rows.find((r) => r.id === project.vocal?.profile_id) || rows.find((r) => r.is_main) || rows[0];
      setSingerId(lead?.id || null);
    }).catch(() => setProfiles([]));
  }, [project.vocal?.profile_id]);
  useEffect(() => { if (ready && mode === null) setMode(backing.arrangement_type === "duet" && backing.partner ? "partner" : "instrumental"); }, [ready, mode, backing]);

  const session = useSingSession({ engine, backing, title, mode: mode || "instrumental", guide });
  const rec = useVocalRecorder({ engine, hasBacking: true, songDuration: session.duration, takeName: `${title} · my vocal`, deviceId });
  const playhead = usePlayhead(engine, rec.phase === "recording");
  const takeChannel = useRef(null);
  takeChannel.current = rec.vocalChannel;
  useEffect(() => () => { if (takeChannel.current != null) engine.setChannelMute(takeChannel.current, true); }, [engine]);
  const locked = rec.phase === "recording" || rec.phase === "arming" || rec.phase === "processing";

  const keep = async () => {
    const take = rec.activeTake;
    if (!take?.blob || keeping) return;
    setKeeping(true);
    engine.pause();
    try {
      await uploadProjectTake(project.id, { blob: take.blob, versionId: version.id, trimHeadSec: take.headSec, backingMode: mode, singerProfileId: singerId });
      rec.markKept(take.id, null);
      toast.success("Your vocal is in the project", { description: project.lyrics_lock ? "ELPM is listening back to check your lyrics." : "Mixed with the music and saved." });
      navigate(`/create/project/${project.id}`);
    } catch (e) { toast.error("Couldn't save that take", { description: safeApiError(e) }); }
    finally { setKeeping(false); }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_380px]">
      <section className="min-w-0 space-y-8">
        <div>
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-rose-300/90"><Mic2 className="h-3.5 w-3.5" /> Sing it myself</p>
          <h1 data-testid="sing-title" className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{title}</h1>
          <p data-testid="sing-subtitle" className="mt-2 text-white/60">Version {version.take} · {project.arrangement_label}</p>
        </div>
        {!ready ? <BackingPrep backing={backing} onPrepare={prepare} busy={busy} /> : (
          <>
            {backing.note ? <p data-testid="backing-note" className="flex items-start gap-2 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-[13px] text-amber-100"><Info className="mt-0.5 h-4 w-4 shrink-0" />{backing.note}</p> : null}
            {session.state === "error" ? <p data-testid="sing-load-error" className="text-rose-300">{session.error}</p> : null}
            <SingOptions backing={backing} mode={mode} onMode={setMode} guide={guide} onGuide={setGuide} profiles={profiles} singerId={singerId} onSinger={setSingerId} disabled={locked} />
            <div className="max-w-md"><MicPicker deviceId={deviceId} onChange={setDeviceId} disabled={locked} /></div>
            <div className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/85 p-4 lg:hidden">
              <LyricsPrompter lyrics={version.lyrics_used || project.lyrics} backing={backing} playhead={playhead} compact testId="lyrics-prompter-mobile" />
            </div>
            <SingRecorder rec={rec} engine={engine} ready={session.state === "ready"} duration={session.duration} playhead={playhead} inst={session.channels.inst} keeping={keeping} onKeep={keep} />
          </>
        )}
      </section>
      <aside className="hidden rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/85 p-5 backdrop-blur-xl lg:sticky lg:top-36 lg:block lg:self-start">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Lyrics</p>
        <LyricsPrompter lyrics={version.lyrics_used || project.lyrics} backing={ready ? backing : null} playhead={playhead} />
      </aside>
    </div>
  );
}

/** "Sing it myself" — record your own lead (or duet part) over a project version, several takes, keep the best. */
export default function SingItMyselfPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setProject(await getProject(id)); } catch (e) { setError(e?.response?.status === 404 ? "We couldn't find that project." : safeApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generated = sortedGenerated(project?.versions);
  const version = generated.find((v) => v.id === params.get("version")) || generated[generated.length - 1];
  const back = `/create/project/${id}`;
  if (error) return <CreatorShell testId="sing-page" back="/create/projects" title="Hmm, that project isn't here" subtitle={error} />;
  if (!project) return <CreatorShell testId="sing-page" back={back}><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening your project…</p></CreatorShell>;
  if (!version) {
    return (
      <CreatorShell testId="sing-page" back={back} title="Make the song first" subtitle="Generate a version of this project, then come back to sing over it.">
        <Link to={back} className="inline-flex rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display font-semibold">Back to the project</Link>
      </CreatorShell>
    );
  }
  return (
    <CreatorShell testId="sing-page" back={back} spine="record" spineDone={["write", "generate"]} wide>
      <VersionPills versions={generated} current={version} onPick={(v) => setParams({ version: v }, { replace: true })} label="Sing over" testId="sing-version" />
      <SingSession key={version.id} project={project} version={version} />
    </CreatorShell>
  );
}
